import { useState, useEffect, useCallback } from "react";

import { getBlobUrl } from "../../utils";

import { Message, Media } from "@twilio/conversations";
import {
  MediaWithBlobUrl,
  MediaLoadingState,
  MediaErrorState,
} from "./definitions";

const useMediaMessage = (message: Message) => {
  const [files, setFiles] = useState<MediaWithBlobUrl[]>([]);
  const [images, setImages] = useState<MediaWithBlobUrl[]>([]);
  const [isLoadingMedia, setLoadingMedia] = useState<MediaLoadingState>({});
  const [errors, setErrors] = useState<MediaErrorState>({});

  // Updates `files` and `images` state with Blob url
  const updateMediaState = (
    updatedMedia: MediaWithBlobUrl,
    mediaContentType: string
  ) => {
    const updatedList = (prevMediaList: MediaWithBlobUrl[]) =>
      prevMediaList.map((media: MediaWithBlobUrl) =>
        media.sid === updatedMedia.sid ? updatedMedia : media
      );

    if (mediaContentType.includes("image")) {
      setImages((prevImages) => updatedList(prevImages));
    } else {
      setFiles((prevFiles) => updatedList(prevFiles));
    }

    setLoadingMedia((prev) => ({ ...prev, [updatedMedia.sid]: false }));
  };

  // Fetchs direct url to the media content from twilio conversations (https://sdk.twilio.com/js/conversations/releases/2.4.0/docs/classes/Media.html#getContentTemporaryUrl)
  const fetchMediaUrl = useCallback(async (media: MediaWithBlobUrl) => {
    setLoadingMedia((prev) => ({ ...prev, [media.sid]: true }));

    try {
      const contentUrl = (await media.getContentTemporaryUrl()) as string;
      const blobUrl = contentUrl ? await getBlobUrl(contentUrl) : "";

      const updatedMedia = {
        sid: media.sid,
        filename: media.filename,
        size: media.size,
        contentUrl,
        blobUrl,
      } as MediaWithBlobUrl;

      updateMediaState(updatedMedia, media.contentType);
    } catch (error) {
      setErrors((prev) => ({ ...prev, [media.sid]: false }));
      console.error(error);
      // Todo: Log error to datadog
    }
  }, []);

  // Sorts media attachments by image and files
  const sortMedia = (mediaList: Media[]) => {
    const newFiles: MediaWithBlobUrl[] = [];
    const newImages: MediaWithBlobUrl[] = [];

    mediaList.forEach((media) => {
      if (media.contentType.includes("image")) {
        newImages.push(media);
      } else {
        newFiles.push(media);
      }
    });

    setFiles(newFiles);
    setImages(newImages);
  };

  // Initiatlize this hook
  useEffect(() => {
    if (!message.attachedMedia || message.attachedMedia.length === 0) {
      return;
    }

    sortMedia(message.attachedMedia);

    const fetchAllMedia = async () => {
      const promises = message.attachedMedia?.map(fetchMediaUrl) || [];
      await Promise.allSettled(promises);
    };

    fetchAllMedia();
  }, [message, fetchMediaUrl]);

  return {
    errors,
    files,
    images,
    isLoadingMedia,
  };
};

export default useMediaMessage;
